import type { V3AppConfigWorkingHours, V3AppConfigWorkingHoursBreak } from '@app/packages/api/v3/types/lib'

import type { Moment } from './moment'

/** Today's window, or `null` on a day support does not work, or when the config carries none. */
export const supportHoursFor = (hours: readonly V3AppConfigWorkingHours[], at: Moment): V3AppConfigWorkingHours | null =>
  hours.find(({ day }) => day === DAYS[at.day()]) ?? null

/**
 * Whether support is off the clock at `at`, in **the reader's own timezone** — the honest reading
 * of a bare «10:00–20:00» printed with no zone beside it, and the same assumption
 * `SupportOfficeHours` makes when it renders them. A break counts as off the clock.
 *
 * No hours for today reads as closed; an empty config reads as closed for every day, so a
 * misconfigured server does not silently promise an answer.
 */
export const isSupportOffHours = (hours: readonly V3AppConfigWorkingHours[], at: Moment): boolean => {
  const today = supportHoursFor(hours, at)
  if (!today) return true
  const minutes = at.hours() * 60 + at.minutes()
  if (minutes < toMinutes(today.start) || minutes >= toMinutes(today.end)) return true
  return (today.breaks ?? []).some(b => minutes >= toMinutes(b.start) && minutes < toMinutes(b.end))
}

/**
 * The windows support actually answers in: the day's hours with its breaks cut out. One window
 * when nothing interrupts it, none on a day support does not work.
 */
export const supportAnsweringIntervals = (hours: V3AppConfigWorkingHours | null): V3AppConfigWorkingHoursBreak[] => {
  if (!hours) return []
  const breaks = [...(hours.breaks ?? [])].sort((a, b) => toMinutes(a.start) - toMinutes(b.start))
  const intervals: V3AppConfigWorkingHoursBreak[] = []
  let start = hours.start

  for (const pause of breaks) {
    if (toMinutes(pause.start) > toMinutes(start)) intervals.push({ start, end: pause.start })
    if (toMinutes(pause.end) > toMinutes(start)) start = pause.end
  }
  if (toMinutes(hours.end) > toMinutes(start)) intervals.push({ start, end: hours.end })

  return intervals
}

const toMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

/** `Moment#day()` is 0 for Sunday; the server's array starts at Monday. */
const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const
